import React from 'react'
import { Link } from '@afterpaytouch/core'
import { ErrorCode } from '@afterpaytouch/portal-api'
import { TFunction, Trans } from 'next-i18next'
import { ReactNode } from 'react'
import { Route } from '../../config/router'
import { AppLink } from '../AppLink'
import { AuthenticationErrors } from '../../model/auth'

export const getUsername = (isMobileLogin: boolean, mobile?: string, email?: string): string => {
  if (isMobileLogin) {
    return mobile ?? ''
  }
  return email ?? ''
}

// Combined error map for both login and password errors
export const errorCodesMap: Partial<Record<ErrorCode, string>> = {
  // Password related errors
  [ErrorCode.AuthenticationFailure]: 'auth',
  [ErrorCode.AuthenticationFailureMaxAttempts]: 'authMaxAttempt',
  [ErrorCode.AccountDisabled]: 'accountDisabled',
  [ErrorCode.AccountClosed]: 'locked',
  [ErrorCode.AccountSuspended]: 'locked',
  [ErrorCode.PaywatchCheckFailed]: 'paywatchCheckFailed',
  [ErrorCode.AccountLocked]: 'accountLocked',

  // Login identity related errors
  [ErrorCode.InvalidEmailAddress]: 'emailNotValid',
  [ErrorCode.RegistrationNotPermitted]: 'registrationNotPermitted',
  [ErrorCode.Error]: 'unknown',
}

export const errorCodeKeys = Object.keys(errorCodesMap) as string[]

// Keep for backward compatibility
export const loginErrorKeysMap = errorCodesMap
export const loginErrorKeys = errorCodeKeys

export const getErrorMessage = (errorCode: ErrorCode, namespace: 'login' | 'password' = 'password'): string => {
  return `${namespace}:error:${errorCodesMap[errorCode] ?? 'unknown'}`
}

/**
 * Converts AuthenticationErrors enum to the corresponding ErrorCode
 * Used to map global auth errors to error messages
 */
export const authErrorToErrorCode = (authError: AuthenticationErrors): ErrorCode => {
  switch (authError) {
    case AuthenticationErrors.IncorrectPassword:
      return ErrorCode.AuthenticationFailure
    case AuthenticationErrors.AccountLocked:
      return ErrorCode.AccountLocked
    case AuthenticationErrors.AccountDisabled:
      return ErrorCode.AccountDisabled
    case AuthenticationErrors.FailureMaxAttempts:
      return ErrorCode.AuthenticationFailureMaxAttempts
    case AuthenticationErrors.TooManyOTPRequests:
      return ErrorCode.TooManyOTPRequests
    case AuthenticationErrors.PaywatchCheckFailed:
      return ErrorCode.PaywatchCheckFailed
    case AuthenticationErrors.AccountClosed:
      return ErrorCode.AccountClosed
    case AuthenticationErrors.OtherError:
    default:
      return ErrorCode.Error
  }
}

export const renderError = (errorMessage: string, t: TFunction, customComponents?: Record<string, ReactNode>): ReactNode => {
  // Default components for both login and password errors
  const defaultComponents = {
    help: <Link key='description-link' target='_blank' kind='Underline' href={t('common:helpUrl')} />,
    helpLanding: <Link key='description-link' target='_blank' kind='Underline' href={t('common:helpLandingUrl')} />,
    helpPaywatch: <Link key='description-link' target='_blank' kind='Underline' href={t('common:urls.paywatchHelpUrl')} />,
    paywatchDesc: <span className='font-black' />,
    reset: <AppLink noMargin inline route={Route.CREATE_PASSWORD_VERIFY} />,
    cashLink: <Link key='description-link' target='_blank' kind='Underline' href={t('common:helpUrl')} />, // Added for login component
  }

  // Merge with any custom components
  const components = customComponents ? { ...defaultComponents, ...customComponents } : defaultComponents

  return <Trans i18nKey={errorMessage} components={components} />
}
